import { billingSideLabel, billingUnitLabel } from "./billingPresentation.js";

export const BILLABLE_CALCULATION_LABELS = {
  inspection: "Inspection",
  loading: "Trajet + chargement",
  delivery: "Trajet + livraison",
  dompeur: "Transport à la tonne",
  return: "Retour",
  preload: "Préchargement",
  tonne_supplement: "Supplément à la tonne",
  voyage_supplement: "Supplément voyage",
  fixed_fee: "Tarif fixe",
};

export function billingNumber(value) {
  return value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value);
}

export function formatBillingNumber(value, maximumFractionDigits = 4) {
  const number = billingNumber(value);
  return number === null ? "—" : new Intl.NumberFormat("fr-CA", { maximumFractionDigits }).format(number);
}

export function formatBillingMoney(value, currency = "CAD") {
  const number = billingNumber(value);
  return number === null ? "À déterminer" : new Intl.NumberFormat("fr-CA", {
    style: "currency", currency: currency || "CAD",
  }).format(number);
}

// Total d'un relevé et ce qui le compose (surtaxe, TPS, TVQ) : carte « Comprendre le montant » et explication au client.
export function statementTotalSummary(totals) {
  const money = (value) => formatBillingMoney(billingNumber(value), totals.currency);
  const rate = (value) => `${new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 3 }).format(billingNumber(value) || 0)} %`;
  const taxed = (billingNumber(totals.gstPercent) || 0) + (billingNumber(totals.qstPercent) || 0) > 0;
  const parts = [
    ["Prestations", totals.subtotal],
    billingNumber(totals.surcharge) ? [`Surtaxe ${rate(totals.surchargePercent)}`, totals.surcharge] : null,
    taxed ? [`TPS ${rate(totals.gstPercent)}`, totals.gst] : null,
    taxed ? [`TVQ ${rate(totals.qstPercent)}`, totals.qst] : null,
  ].filter(Boolean);
  return { label: taxed ? "Total du relevé, taxes comprises" : "Total du relevé, avant taxes", total: money(totals.total),
    detail: parts.length > 1 ? parts.map(([label, value]) => `${label} ${money(value)}`).join(" + ") : "" };
}

export function formatBillingDuration(value) {
  const seconds = billingNumber(value);
  if (seconds === null || seconds < 0) return "—";
  // Billing shows minutes only; « ≈ » flags a rounded value.
  const rounded = Math.round(seconds / 60) * 60;
  if (!rounded && seconds > 0) return "< 1 min";
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const parts = [hours ? `${hours} h` : "", minutes ? `${minutes} min` : ""].filter(Boolean);
  return `${Math.abs(seconds - rounded) > 0.001 ? "≈ " : ""}${parts.join(" ") || "0 min"}`;
}

export function formatBillingQuantity(value, unit) {
  return unit === "heure" ? formatBillingDuration(billingNumber(value) === null ? null : Number(value) * 3600)
    : `${formatBillingNumber(value, 6)} ${billingUnitLabel(unit, value)}`;
}

export function billingRateLabel(value, unit, currency = "CAD") {
  const suffix = unit === "heure" ? "h" : unit === "tonne" ? "t" : billingUnitLabel(unit, 1);
  const rate = billingNumber(value);
  if (rate === null) return "Tarif à déterminer";
  return `${new Intl.NumberFormat("fr-CA", { style: "currency", currency, maximumFractionDigits: 4 }).format(rate)}/${suffix}`;
}

export function billableCalculationLabel(item) {
  return BILLABLE_CALCULATION_LABELS[item.calculation_details?.kind]
    || billingSideLabel(item.billing_side) || "Prestation";
}

export function billableCalculationComponents(item) {
  return (item.billing_components?.length ? item.billing_components : [item]).map((component) => ({
    ...component,
    currency_code: item.currency_code || "CAD",
    line_total: item.pricing_pending || item.line_total == null ? null : component.line_total,
  }));
}

/** Sum amounts already rounded by the server; never reprice quantities in the UI. */
export function summarizeBillableCalculation(items) {
  const groups = new Map();
  for (const item of items) {
    for (const component of billableCalculationComponents(item)) {
      const unit = component.billing_unit || "";
      const rate = billingNumber(component.unit_rate);
      const key = JSON.stringify([unit, rate, component.currency_code]);
      if (!groups.has(key)) groups.set(key, {
        key, unit, rate, currency: component.currency_code, quantity: 0, cents: 0, amountKnown: true,
      });
      const group = groups.get(key);
      const quantity = billingNumber(component.quantity);
      group.quantity = group.quantity === null || quantity === null ? null : group.quantity + quantity;
      const amount = billingNumber(component.line_total);
      group.amountKnown &&= amount !== null;
      if (amount !== null) group.cents += Math.round(amount * 100);
    }
  }
  return [...groups.values()].map((group) => ({ ...group, amount: group.amountKnown ? group.cents / 100 : null }));
}

/** Presentation groups retain their source components so a selected amount is fully traceable. */
export function groupBillableCalculationPosts(items) {
  const buckets = new Map();
  for (const item of items) {
    billableCalculationComponents(item).forEach((component, index) => {
      const kind = item.calculation_details?.kind || "";
      const side = item.billing_side || "";
      const key = JSON.stringify([kind, side, component.billing_unit || "", billingNumber(component.unit_rate), component.currency_code]);
      if (!buckets.has(key)) buckets.set(key, { key, kind, side, label: billableCalculationLabel(item), items: [] });
      buckets.get(key).items.push({
        ...item, ...component, id: `${item.id}:${index}`, source_item_id: item.id,
        // Only this component belongs to the selected post, not the item's other charges.
        billing_components: [],
      });
    });
  }
  const order = Object.keys(BILLABLE_CALCULATION_LABELS);
  const position = (kind) => order.includes(kind) ? order.indexOf(kind) : order.length;
  return [...buckets.values()].map((bucket) => ({
    ...summarizeBillableCalculation(bucket.items)[0], ...bucket,
  })).sort((a, b) => position(a.kind) - position(b.kind)
    || a.side.localeCompare(b.side) || a.unit.localeCompare(b.unit)
    || (a.rate ?? Infinity) - (b.rate ?? Infinity) || a.currency.localeCompare(b.currency));
}

/** Hours billed across items: undefined without hourly charges, null when a quantity is unknown. */
export function billableHoursTotal(items) {
  const hours = summarizeBillableCalculation(items).filter((group) => group.unit === "heure");
  if (!hours.length) return undefined;
  return hours.some((group) => group.quantity === null) ? null : hours.reduce((sum, group) => sum + group.quantity, 0);
}

/** Tons billed by dump trucks across items: undefined without any, null when a quantity is unknown. */
export function billableDompeurTons(items) {
  const groups = summarizeBillableCalculation(items.filter((item) => item.calculation_details?.kind === "dompeur"));
  if (!groups.length) return undefined;
  return groups.some((group) => group.quantity === null) ? null : groups.reduce((sum, group) => sum + group.quantity, 0);
}

export function billableCalculationTotals(items) {
  const totals = new Map();
  for (const item of items) {
    const currencyCode = item.currency_code || "CAD";
    if (!totals.has(currencyCode)) totals.set(currencyCode, { currencyCode, cents: 0, known: true });
    const total = totals.get(currencyCode);
    const amount = item.pricing_pending ? null : billingNumber(item.line_total);
    total.known &&= amount !== null;
    if (amount !== null) total.cents += Math.round(amount * 100);
  }
  return [...totals.values()].map(({ currencyCode, cents, known }) => ({ currencyCode, amount: known ? cents / 100 : null }));
}

function stepOrder(item) {
  const kind = item.calculation_details?.kind;
  if (kind === "inspection" || kind === "preload") return "0";
  if (kind === "return") return "2";
  const supplement = { tonne_supplement: "1", fixed_fee: "2", voyage_supplement: "3" }[kind] || "0";
  return `1:${item.calculation_details?.block?.start || ""}:${supplement}:${item.order_reference || ""}`;
}

export function groupBillableCalculationSteps(items) {
  const groups = new Map();
  for (const item of items) {
    // Un Chargé PM est facturé avec sa livraison, mais reste dans la tournée du jour où il est chargé.
    const date = item.calculation_details?.execution_date || item.service_date || "";
    const key = `${date}:${item.calculation_details?.revision_id || item.source_type || "legacy"}`;
    if (!groups.has(key)) groups.set(key, { key, date, items: [] });
    groups.get(key).items.push(item);
  }
  return [...groups.values()].sort((a, b) => b.date.localeCompare(a.date)).map((group) => ({
    ...group, items: group.items.sort((a, b) => stepOrder(a).localeCompare(stepOrder(b))),
  }));
}

export function billableAllocationLabel(item) {
  const details = item.calculation_details;
  if (!["inspection", "loading", "delivery", "return", "preload"].includes(details?.kind)) return "";
  const numerator = billingNumber(details?.coefficient?.numerator);
  const denominator = billingNumber(details?.coefficient?.denominator);
  if (numerator === null || denominator === null || denominator <= 0) return "";
  const share = formatBillingNumber(numerator / denominator * 100, 2);
  if (details.kind === "delivery") return `${formatBillingNumber(numerator)} t sur ${formatBillingNumber(denominator)} t livrées : ${share} % du bloc attribué à cette commande.`;
  if (["inspection", "return"].includes(details.kind)) return `${formatBillingDuration(numerator)} sur ${formatBillingDuration(denominator)} de temps direct : ${share} % du temps commun attribué à cette meunerie.`;
  return `${share} % de cette prestation attribué à cette meunerie.`;
}
